import { Checkbox } from '../../atoms/Checkbox/Checkbox';
import { Icon } from '../../atoms/Icon/Icon';
import styles from './SelectMenuItem.module.css';

/**
 * Figma: `_Select menu item` (96:2640) — the row inside the `Select` dropdown.
 * Variants: Selected (True, False) × Type (Multi Select, Single Select) × State
 * (Default, Hover, Disabled).
 *
 * The `_` prefix marks it internal, so it stays out of the package surface —
 * compose it through `Select`.
 *
 * **Reworked 2026-09-23**, and the row is a different shape:
 *
 * 1. **The row is 22 tall, not 30** — `Content` pads `space/4` rather than
 *    `space/8` around the same 14px line.
 * 2. **The label is `Regular`**, not `Medium`, in every variant including
 *    `Selected=True`. The mark on the row is what says it is chosen.
 * 3. **`space/6` between the parts**, where the old row used `space/8`.
 * 4. **`Type` became the how-you-choose axis** — see below.
 *
 * **`Type` says how the row is chosen**, in a second pass the same day:
 *
 * - **`Single Select`** keeps the trailing `check` glyph (14x14, `icon/info`,
 *   `icon/disabled` when disabled), on the selected row only.
 * - **`Multi Select`** drops it for a leading `Checkbox` (14x14, `Text=False`)
 *   whose `Checked` follows `Selected` — see `presentational` on `Checkbox`
 *   for how a checkbox lives inside an `option` without being announced or
 *   focused twice.
 *
 * `Select` picks between them from its own `multiple`, so the row and the
 * control can never disagree about which one the user is looking at.
 *
 * The row's `🔀 Icon swap` property is orphaned — no layer references it and no
 * variant draws a leading icon — so `icon` is gone from this component.
 * `Select`'s own `Type=Icon leading` is unaffected: that icon is on the
 * trigger, which still draws it.
 *
 * **One deviation.** Figma's unselected `Single Select` rows are 20 tall
 * against the selected row's 22, because the tick is deleted rather than
 * hidden and the row hugs the 12px label. A list whose rows change height when
 * you pick one is a jump, so the slot is held open here (`visibility: hidden`)
 * and every row is 22. Flagged in plan.md §7.
 *
 * `State=Hover` is CSS and `State=Disabled` is real state.
 */
export type SelectMenuItemType = 'singleSelect' | 'multiSelect';
export type SelectMenuItemState = 'default' | 'hover';

export type SelectMenuItemProps = {
  label: string;
  /** Figma's `Supporting text` toggle — the dimmer text after the label. */
  supportingText?: string;
  /** Figma `Type`. `Select` derives it from `multiple`. */
  type?: SelectMenuItemType;
  selected?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  /** Force the hover appearance for documentation and visual regression only. */
  state?: SelectMenuItemState;
  id?: string;
  className?: string;
};

export function SelectMenuItem({
  label,
  supportingText,
  type = 'singleSelect',
  selected = false,
  disabled = false,
  onSelect,
  state,
  id,
  className,
}: SelectMenuItemProps) {
  const multi = type === 'multiSelect';

  const contentClasses = [
    styles.content,
    selected ? styles.selected : null,
    disabled ? styles.disabled : null,
    state === 'hover' ? styles.hover : null,
    // Live hover only when no state is forced and the row can be chosen.
    !disabled && state === undefined ? styles.interactive : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    /*
     * Not in Figma — Figma has no concept of a listbox. `option` is the role a
     * row of a single-select list needs, and it carries its own selected state.
     */
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- keyboard is handled by the composite widget (roving focus / native control); the pointer handler is a convenience. Axe checks run in Storybook and E2E.
    <li
      className={[styles.item, className].filter(Boolean).join(' ')}
      id={id}
      role="option"
      aria-selected={selected}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onSelect}
    >
      <span className={contentClasses}>
        <span className={[styles.textAndSupporting, multi ? null : styles.single].filter(Boolean).join(' ')}>
          {multi && (
            /*
             * Figma's `Checkbox` `Text=False`. `presentational` because the `li`
             * above is already `aria-selected`; without it the same state would
             * be announced twice and the input would be a tab stop inside a
             * listbox.
             */
            <Checkbox
              presentational
              checked={selected}
              disabled={disabled}
              className={styles.checkbox}
            />
          )}
          <span className={styles.label}>{label}</span>
          {supportingText !== undefined && (
            <span className={styles.supporting}>{supportingText}</span>
          )}
          {!multi && (
            /*
             * Figma deletes this on an unselected row; it is held open instead,
             * so picking a row does not make it 2px taller. `aria-hidden`
             * because `aria-selected` on the row already says it.
             */
            <Icon
              name="check"
              className={[styles.check, selected ? null : styles.checkHidden]
                .filter(Boolean)
                .join(' ')}
              aria-hidden
            />
          )}
        </span>
      </span>
    </li>
  );
}
